import type { ReactElement } from 'react';
import { PackageDetailsPackageSpecs } from './packageDetails/PackageDetailsPackageSpecs';
import { PackageDetailsServiceList } from './packageDetails/PackageDetailsServiceList';
import styles from './PackageDetails.module.scss';
import type { SanitizedHtml } from 'common/types';
import type {
  MeetingPackage,
  VenuePackage,
  CelebrationPackage,
  ExperiencePackage,
  Service,
  PackageSelectComponent
} from 'packages/types';

type PackageWithServices = MeetingPackage | VenuePackage;
type PackageWithoutServices = CelebrationPackage | ExperiencePackage;

type PropsWithServices = {
  pkg: PackageWithServices;
  allServices: Service[];
  PackageSelectComponent?: PackageSelectComponent;
};
type PropsWithoutServices = {
  pkg: PackageWithoutServices;
  PackageSelectComponent?: PackageSelectComponent;
};

type Props = PropsWithServices | PropsWithoutServices;

export function PackageDetails(props: Props): ReactElement {
  return (
    <div className={styles.details}>
      {
        // `allServices` only appears on PropsWithServices, so its presence is
        // enough to narrow both it and `props.pkg.serviceIds` at once — unlike
        // Flow, TS's `in` narrows a union by key existence directly.
        'allServices' in props ? (
          <div className={styles.serviceListContainer}>
            <PackageDetailsServiceList
              allServices={props.allServices}
              includedServiceIds={props.pkg.serviceIds}
            />
          </div>
        ) : null
      }
      <div className={styles.infoContainer}>
        {isDescriptionEmpty(props.pkg.description) ? null : (
          <div className={styles.descriptionContainer}>
            <div className={styles.description}>
              <div dangerouslySetInnerHTML={props.pkg.description} />
            </div>
          </div>
        )}
        <div>
          <PackageDetailsPackageSpecs
            availability={props.pkg.availability}
            duration={props.pkg.duration}
            capacity={props.pkg.capacity}
            pricing={props.pkg.pricing}
          />
        </div>
        {props.PackageSelectComponent ? (
          <div className={styles.packageSelectionContainer}>
            <props.PackageSelectComponent pkg={props.pkg} />
          </div>
        ) : null}
      </div>
    </div>
  );
}

// Empty rich text editor content HTML
const EMPTY_HTML = '<div><br></div>';
function isDescriptionEmpty(description: SanitizedHtml) {
  return (
    !description ||
    description.__html === '' ||
    description.__html === EMPTY_HTML
  );
}
